// deps
import React, { ReactElement, useCallback, useContext, memo, useMemo } from "react";
import { NavigationScreenLinkTypes, NavigationTypes } from "@swiggy-private/native-bridge";

// utils
import { getBridgeSdk } from "@swiggy-private/checkout-core";

// ui components
import { ToingHeaderContent } from "@swiggy-private/cart-ui";

// analytics
import { clickCartHeaderDropdown, impressionCartHeader } from "@cart-client/analytics/events";
import { AnalyticsContext } from "@swiggy-private/checkout-core";

// types
import { CartUIWrapperProps } from "../cart-ui-wrapper/types";

// constants
import { CartAddressContext } from "@cart-client/contexts/CartAddressContext";
import { CartContext } from "@cart-client/contexts/CartContext";
import { isToingRtbXp } from "@cart-client/helpers/ToingHeader";
import { useAddressNudgeConfig } from "@cart-client/hooks/useAddressNudgeConfig";
import { FreeCashRemovalPopupVariant } from "@swiggy-private/cart-types";
import { useFreeCashRemovalPopup } from "@cart-client/contexts/FreeCashRemovalPopupContext";

function CartHeaderContentContainer(props: Omit<CartUIWrapperProps, "children">): ReactElement | null {
    const {
        restaurantName,
        restaurantId,
        selectedAddress,
        onAddressDropdownClick,
        disableRedirectionToMenu,
        isFreeCashApplicable,
    } = props;
    const { showPopup, hidePopup } = useFreeCashRemovalPopup();
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);

    const { cartAddressState } = useContext(CartAddressContext);
    const { cartState } = useContext(CartContext);
    const isRtbXpEnabled = useMemo(() => isToingRtbXp(cartState?.reviewedCart ?? null), [cartState?.reviewedCart]);

    const showAddressNudgeIcon = cartAddressState?.addressNudgeConfig?.showAddressNudgeIcon;

    const onImpressionCartHeader = useCallback(() => {
        impressionCartHeader(impressionEvent);
    }, [impressionEvent]);

    const onClickCartHeaderDropdown = useCallback(() => {
        clickCartHeaderDropdown(clickEvent);
    }, [clickEvent]);

    const onRestaurantNameClick = useCallback(() => {
        if (disableRedirectionToMenu) return;

        getBridgeSdk().navigateToScreen({
            type: NavigationTypes.SCREEN,
            link: NavigationScreenLinkTypes.MENU,
            params: {
                id: String(restaurantId),
                fromCart: true,
            },
        });
    }, [disableRedirectionToMenu, restaurantId]);

    const handleAddressDropdownClick = useCallback(() => {
        if (isFreeCashApplicable) {
            showPopup({
                variant: FreeCashRemovalPopupVariant.MENU_NAVIGATION,
                onPrimaryClick: () => {
                    hidePopup();
                    onAddressDropdownClick?.();
                },
                onSecondaryClick: hidePopup,
                analyticsParams: {
                    warningTrigger: "address_dropdown",
                },
            });
            return;
        }
        onAddressDropdownClick?.();
    }, [isFreeCashApplicable, showPopup, hidePopup, onAddressDropdownClick]);

    useAddressNudgeConfig();

    if (restaurantName) {
        return (
            <ToingHeaderContent
                restaurantName={restaurantName}
                selectedAddress={selectedAddress}
                onAddressDropdownClick={handleAddressDropdownClick}
                handleRestaurantNameClick={onRestaurantNameClick}
                onImpressionCartHeaderContent={onImpressionCartHeader}
                onClickCartHeaderDropdown={onClickCartHeaderDropdown}
                showAddressNudgeIcon={showAddressNudgeIcon ?? false}
                isRtbXpEnabled={isRtbXpEnabled}
            />
        );
    }
    return null;
}

export default memo(CartHeaderContentContainer);
